'use client';

import type { KeyboardEvent, Ref } from 'react';
import type { CatalogueItem } from './homepage-types';

import Image from '@eliadmin/core/src/components/image/image-wrapped';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useEffect, useRef, useState } from 'react';
import { CatalogueThumbnails } from './catalogue-thumbnails';
import { PillLink } from './ui/pill-link';
import { usePdfDocument, usePdfPageImages } from './use-pdf-document';

/** Rendered page width: the stage is at most 460 CSS px, rendered for 2× screens. */
const PAGE_WIDTH = 920;
const THUMB_WIDTH = 128;
const PAGE_CACHE = 8;

const navButtonClassName =
  'h-[38px] w-[38px] flex-none cursor-pointer rounded-full border-[1.5px] border-[#e6eaee] bg-white text-[1.2rem] transition-all duration-200 hover:border-[#14181c] hover:bg-[#14181c] hover:text-white disabled:pointer-events-none disabled:opacity-40 min-[641px]:h-12 min-[641px]:w-12';
const figureClassName =
  'pointer-events-none absolute inset-0 m-0 opacity-0 transition-[opacity,transform] [transform:perspective(1200px)_rotateY(-12deg)_translateX(20px)] [transition-duration:450ms]';
const pageImageClassName =
  'absolute inset-0 h-full w-full rounded-[6px_14px_14px_6px] object-contain shadow-[0_30px_50px_-20px_rgba(0,0,0,.45),inset_0_0_0_1px_rgba(0,0,0,.05)]';

type CatalogueViewerProps = {
  ref?: Ref<HTMLDivElement>;
  catalogue: CatalogueItem;
  title: string;
  text: string;
  actionLabel: string;
  previousLabel: string;
  nextLabel: string;
};

/** Flipbook of the selected catalogue: every page of its PDF, one after another. */
export function CatalogueViewer({
  ref,
  catalogue,
  title,
  text,
  actionLabel,
  previousLabel,
  nextLabel,
}: CatalogueViewerProps) {
  const stageRef = useRef<HTMLDivElement>(null);
  const [isNear, setIsNear] = useState(false);
  const [page, setPage] = useState(1);
  const [pageRatio, setPageRatio] = useState<string>();
  const { doc, failed } = usePdfDocument(isNear ? catalogue.pdfUrl : null);
  const pages = usePdfPageImages(doc, PAGE_WIDTH, PAGE_CACHE);
  const thumbs = usePdfPageImages(doc, THUMB_WIDTH);
  const pageCount = doc?.numPages ?? 0;
  const requestPages = pages.request;

  // The PDF (and pdf.js) start loading when the flipbook comes near the viewport.
  useEffect(() => {
    const stage = stageRef.current;
    if (!stage) return;

    const observer = new IntersectionObserver(
      entries => {
        if (entries.some(entry => entry.isIntersecting)) {
          setIsNear(true);
          observer.disconnect();
        }
      },
      { rootMargin: '600px 0px' },
    );
    observer.observe(stage);
    return () => observer.disconnect();
  }, []);

  // The stage takes the catalogue's page format (A4 and others) instead of the design's 595 × 808.
  useEffect(() => {
    if (!doc) return;

    let cancelled = false;
    doc.getPage(1).then(
      first => {
        const { width, height } = first.getViewport({ scale: 1 });
        if (!cancelled) setPageRatio(`${width} / ${height}`);
      },
      () => {},
    );
    return () => {
      cancelled = true;
    };
  }, [doc]);

  useEffect(() => {
    if (!pageCount) return;
    requestPages?.([page, page + 1, page - 1].filter(n => n >= 1 && n <= pageCount));
  }, [page, pageCount, requestPages]);

  const goTo = (target: number) => setPage(Math.min(Math.max(target, 1), pageCount || 1));

  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'ArrowRight') goTo(page + 1);
    else if (event.key === 'ArrowLeft') goTo(page - 1);
    else return;
    event.preventDefault();
  };

  const visiblePages = pageCount
    ? [page - 1, page, page + 1].filter(n => n >= 1 && n <= pageCount)
    : [1];
  const caption = pageCount ? `${catalogue.title} · ${page} / ${pageCount}` : catalogue.title;
  // A PDF that cannot load leaves the cover; the button still opens the whole catalogue.
  const isLoading = !failed && !pages.urls.has(page);

  return (
    <div
      ref={ref}
      className="mb-7 grid grid-cols-1 items-center gap-x-10 gap-y-5 rounded-3xl border border-[#e6eaee] bg-white p-5 min-[901px]:grid-cols-[300px_1fr] min-[901px]:p-7"
    >
      <div>
        <h3 className="m-0 mb-2.5 text-[1.6rem] font-extrabold tracking-[-0.02em]">{title}</h3>
        <p className="m-0 mb-5 leading-[1.6] text-[#5f6b76]">{text}</p>
        <PillLink href={catalogue.href} target="_blank" rel="noopener">
          {actionLabel}
        </PillLink>
      </div>
      <div className="flex items-center justify-center gap-3.5">
        <button
          type="button"
          aria-label={previousLabel}
          disabled={page <= 1}
          onClick={() => goTo(page - 1)}
          className={navButtonClassName}
        >
          ←
        </button>
        <div
          ref={stageRef}
          tabIndex={0}
          role="group"
          aria-label={caption}
          aria-busy={isLoading}
          onKeyDown={onKeyDown}
          onClick={() => goTo(page + 1)}
          style={pageRatio ? { aspectRatio: pageRatio } : undefined}
          className="relative aspect-[595/808] w-[min(100%,460px)] cursor-pointer rounded-[6px_14px_14px_6px] outline-offset-4"
        >
          {visiblePages.map(pageNumber => {
            const url = pages.urls.get(pageNumber);
            return (
              <figure
                key={pageNumber}
                aria-hidden={pageNumber !== page}
                className={cn(
                  figureClassName,
                  pageNumber === page && 'pointer-events-auto opacity-100 [transform:none]',
                )}
              >
                {url ? (
                  // Object URL of a page rendered by pdf.js – nothing for the image optimizer.
                  // eslint-disable-next-line @next/next/no-img-element
                  <img
                    src={url}
                    alt={`${catalogue.title} – ${pageNumber}`}
                    className={pageImageClassName}
                  />
                ) : (
                  pageNumber === 1 &&
                  catalogue.image && (
                    <Image
                      src={catalogue.image.url}
                      alt={catalogue.image.alt}
                      fill
                      sizes="460px"
                      className={pageImageClassName}
                    />
                  )
                )}
              </figure>
            );
          })}
          {isLoading && (
            <span
              aria-hidden
              className="absolute left-1/2 top-1/2 h-9 w-9 -translate-x-1/2 -translate-y-1/2 animate-spin rounded-full border-[3px] border-[#e6eaee] border-t-[#00a1e6]"
            />
          )}
          <p className="absolute inset-x-0 -bottom-[30px] m-0 text-center text-[.82rem] text-[#5f6b76]">
            {caption}
          </p>
        </div>
        <button
          type="button"
          aria-label={nextLabel}
          disabled={!pageCount || page >= pageCount}
          onClick={() => goTo(page + 1)}
          className={navButtonClassName}
        >
          →
        </button>
      </div>
      {/* The strip's top padding holds the caption; a PDF that cannot load leaves nothing to wait for. */}
      <CatalogueThumbnails
        pageCount={pageCount}
        page={page}
        urls={thumbs.urls}
        request={thumbs.request}
        onSelect={goTo}
        className={failed ? undefined : 'min-h-[120px]'}
      />
    </div>
  );
}
